import { Report, ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PageLoader } from '@/components/shared/PageLoader';
import { ReportCard } from './ReportCard';
import { NoReportFound } from '../shared/NoReportFound';
import { useFilteredReportsByType } from '../hooks/useFilteredReportsByType';

type ReportsListProps = {
  selectedReportType: ReportInternalType;
  status: ReportStatus;
  searchTerm?: string | undefined;
  groupId?: string | undefined;
  groupName?: string | undefined;
  selectedReportIds?: string[];
  setSelectedReportIds?: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedReportsMap?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};

export const ReportsList = ({
  selectedReportType,
  status,
  searchTerm,
  groupId,
  groupName,
  selectedReportIds = [],
  setSelectedReportIds = () => {},
  setSelectedReportsMap = () => {},
}: ReportsListProps) => {
  const { reports, loading, error } = useFilteredReportsByType(
    selectedReportType,
    status,
    searchTerm,
    groupId,
    groupName
  );

  if (loading && reports && reports.length === 0) {
    return (
      <Stack direction="row" justifyContent="center" marginTop={8}>
        <PageLoader />
      </Stack>
    );
  }

  if (!loading && (!reports || error)) {
    return (
      <Stack justifyContent="center" marginTop="20%" textAlign="center">
        <Typography my={2} variant="h6">
          An unknown error has occurred, please contact support
        </Typography>
        <Typography>Error code: {error?.message || 'N/A'}</Typography>
      </Stack>
    );
  }

  const handleCheckboxChange = (reportId: string, checked: boolean, reportName?: string) => {
    if (checked) {
      setSelectedReportIds(prev => [...prev, reportId]);
      if (reportName) {
        setSelectedReportsMap(prev => ({ ...prev, [reportId]: reportName }));
      }
    } else {
      setSelectedReportIds(prev => prev.filter(id => id !== reportId));
      setSelectedReportsMap(prev => {
        const newMap = { ...prev };
        delete newMap[reportId];
        return newMap;
      });
    }
  };

  return (
    <Grid container rowSpacing={3} columnSpacing={4} paddingX={1}>
      {reports.length === 0 ? (
        <Grid size={{ xs: 12 }} display="flex" justifyContent="center" alignItems="center">
          <NoReportFound />
        </Grid>
      ) : (
        <>
          {reports.map((report) => {
            return (
              <Grid key={report?.id} size={{ xs: 12, sm: 6, md: 3, lg: 3, xl: 3 }}>
                <ReportCard
                  report={report as Report}
                  renderCheckBox={report?.internalType === ReportInternalType.Component}
                  isChecked={selectedReportIds.includes(report?.id || '')}
                  onCheckboxChange={handleCheckboxChange}
                />
              </Grid>
            );
          })}
        </>
      )}
    </Grid>
  );
};
